import { useState, useEffect, useRef } from "react";

/* ═══════════════════════════════════════════════════════
   RESPONSIVE HOOK
═══════════════════════════════════════════════════════ */
function useBreakpoint() {
  const [w, setW] = useState(typeof window !== 'undefined' ? window.innerWidth : 1200);
  useEffect(() => {
    const handler = () => setW(window.innerWidth);
    window.addEventListener('resize', handler, { passive: true });
    return () => window.removeEventListener('resize', handler);
  }, []);
  return { isMobile: w < 768, isTablet: w < 1024, width: w };
}

/* ═══════════════════════════════════════════════════════
   GLOBAL STYLES
═══════════════════════════════════════════════════════ */
const STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=Cinzel:wght@400;600;700;900&family=Jost:wght@300;400;500;600;700&display=swap');

  *, *::before, *::after { margin: 0; padding: 0; box-sizing: border-box; -webkit-tap-highlight-color: transparent; }
  html { scroll-behavior: smooth; -webkit-text-size-adjust: 100%; }
  body { background: #060d1f; color: #e2e8f0; font-family: 'Jost', sans-serif; overflow-x: hidden; }
  :root {
    --navy: #060d1f; --navy-mid: #0d1b3e;
    --gold: #f59e0b; --amber: #f97316; --cream: #fde68a;
  }

  @keyframes sunRiseAnim {
    0%   { transform: translateX(-50%) translateY(180px); opacity: 0; }
    25%  { opacity: 1; }
    100% { transform: translateX(-50%) translateY(-90px); opacity: 1;
           box-shadow: 0 0 100px 60px rgba(249,115,22,0.5), 0 0 200px 120px rgba(245,158,11,0.25); }
  }
  @keyframes loadBar   { 0% { width:0% } 100% { width:100% } }
  @keyframes logoReveal { 0% { opacity:0; transform:translateY(30px) scale(0.92); filter:blur(8px) }
                          100% { opacity:1; transform:translateY(0) scale(1); filter:blur(0) } }
  @keyframes fadeOut   { 0% { opacity:1 } 100% { opacity:0; pointer-events:none; visibility:hidden } }
  @keyframes fadeInUp  { from { opacity:0; transform:translateY(40px) } to { opacity:1; transform:translateY(0) } }
  @keyframes float     { 0%,100% { transform:translateY(0) } 50% { transform:translateY(-18px) } }
  @keyframes shimmer   { 0% { background-position:-300% center } 100% { background-position:300% center } }
  @keyframes pulse     { 0%,100% { transform:scale(1); opacity:0.6 } 50% { transform:scale(1.12); opacity:1 } }
  @keyframes spinSlow  { from { transform:rotate(0deg) } to { transform:rotate(360deg) } }
  @keyframes glowPulse { 0%,100% { box-shadow:0 0 30px rgba(245,158,11,0.12) }
                         50% { box-shadow:0 0 80px rgba(245,158,11,0.3) } }
  @keyframes particleRise {
    0%   { transform:translateY(0) scale(0); opacity:0 }
    10%  { opacity:0.8; transform:scale(1) }
    90%  { opacity:0.3 }
    100% { transform:translateY(-110vh) scale(0.3); opacity:0 }
  }
  @keyframes gradientShift { 0%,100% { background-position:0% 50% } 50% { background-position:100% 50% } }
  @keyframes rayPulse  { 0%,100% { opacity:0.4 } 50% { opacity:0.9 } }
  @keyframes menuSlide { from { opacity:0; transform:translateY(-14px) } to { opacity:1; transform:translateY(0) } }
  @keyframes mobileCta { from { transform:translateY(80px) } to { transform:translateY(0) } }

  .gold-text {
    background: linear-gradient(120deg, #f59e0b 0%, #fde68a 40%, #f97316 70%, #f59e0b 100%);
    background-size: 300% auto;
    -webkit-background-clip: text; -webkit-text-fill-color: transparent;
    background-clip: text; animation: shimmer 4s linear infinite;
  }
  .hero-bg {
    background: linear-gradient(135deg, #060d1f 0%, #0d1b3e 50%, #060d1f 100%);
    background-size: 200% 200%; animation: gradientShift 10s ease infinite;
  }
  .glass-card {
    background: rgba(255,255,255,0.025); backdrop-filter: blur(16px);
    -webkit-backdrop-filter: blur(16px); border: 1px solid rgba(245,158,11,0.12);
    transition: all 0.45s cubic-bezier(0.25,0.8,0.25,1);
  }
  @media (hover: hover) {
    .glass-card:hover {
      background: rgba(245,158,11,0.05); border-color: rgba(245,158,11,0.4);
      transform: translateY(-8px);
      box-shadow: 0 25px 70px rgba(245,158,11,0.12);
    }
  }
  .nav-link {
    position: relative; color: rgba(255,255,255,0.65);
    font-family: 'Jost', sans-serif; font-weight: 500; font-size: 0.8rem;
    letter-spacing: 0.12em; text-transform: uppercase;
    background: none; border: none; cursor: pointer; padding: 6px 0; transition: color 0.3s;
  }
  .nav-link::after {
    content:''; position:absolute; bottom:0; left:0;
    width:0; height:1px; background:linear-gradient(to right,#f59e0b,#f97316); transition:width 0.35s;
  }
  .nav-link:hover { color:#f59e0b; }
  .nav-link:hover::after { width:100%; }

  .btn-gold {
    background: linear-gradient(135deg, #f59e0b 0%, #f97316 100%);
    border: none; color: #060d1f; font-family: 'Cinzel', serif; font-weight: 700;
    font-size: 0.75rem; letter-spacing: 0.15em; text-transform: uppercase;
    cursor: pointer; border-radius: 6px; padding: 15px 32px;
    transition: all 0.3s; position: relative; overflow: hidden;
    touch-action: manipulation; min-height: 48px; -webkit-appearance: none;
  }
  .btn-gold::before {
    content:''; position:absolute; top:0; left:-100%;
    width:100%; height:100%;
    background:linear-gradient(90deg,transparent,rgba(255,255,255,0.3),transparent);
    transition:left 0.5s;
  }
  .btn-gold:hover  { transform:translateY(-3px); box-shadow:0 12px 35px rgba(245,158,11,0.45); }
  .btn-gold:active { transform:scale(0.97); }
  .btn-gold:hover::before { left:100%; }

  .btn-outline-gold {
    background: transparent; border: 1px solid rgba(245,158,11,0.45); color: #f59e0b;
    font-family: 'Cinzel', serif; font-weight: 600; font-size: 0.75rem;
    letter-spacing: 0.15em; text-transform: uppercase;
    cursor: pointer; border-radius: 6px; padding: 15px 32px;
    transition: all 0.3s; touch-action: manipulation; min-height: 48px;
  }
  .btn-outline-gold:hover  { background:rgba(245,158,11,0.08); border-color:#f59e0b; transform:translateY(-3px); }
  .btn-outline-gold:active { transform:scale(0.97); }

  .reveal { opacity:0; transform:translateY(40px); transition: opacity 0.85s ease, transform 0.85s ease; }
  .reveal.visible { opacity:1; transform:translateY(0); }

  input[type="range"] {
    -webkit-appearance: none; appearance: none; width:100%; height:6px;
    background: linear-gradient(to right, #f59e0b var(--pct,50%), rgba(245,158,11,0.15) var(--pct,50%));
    border-radius:6px; outline:none; cursor:pointer; touch-action:pan-x;
  }
  input[type="range"]::-webkit-slider-thumb {
    -webkit-appearance:none; appearance:none;
    width:28px; height:28px; border-radius:50%;
    background:linear-gradient(135deg,#f59e0b,#f97316);
    cursor:pointer; box-shadow:0 0 12px rgba(245,158,11,0.6); border:2px solid #fde68a;
  }
  input[type="range"]::-moz-range-thumb {
    width:26px; height:26px; border-radius:50%;
    background:linear-gradient(135deg,#f59e0b,#f97316);
    cursor:pointer; border:2px solid #fde68a;
  }

  ::-webkit-scrollbar { width:3px; }
  ::-webkit-scrollbar-track { background:#030810; }
  ::-webkit-scrollbar-thumb { background:linear-gradient(to bottom,#f59e0b,#f97316); border-radius:3px; }

  .mobile-menu {
    position:fixed; top:0; left:0; right:0; bottom:0; z-index:98;
    background:rgba(4,9,22,0.97); backdrop-filter:blur(24px);
    display:flex; flex-direction:column; align-items:center; justify-content:center;
    gap:4px; animation:menuSlide 0.3s ease; padding:20px;
  }
  .mobile-nav-btn {
    font-family:'Cinzel',serif; font-weight:700;
    font-size:clamp(1.4rem,7vw,2rem); color:rgba(255,255,255,0.6);
    background:none; border:none; cursor:pointer; padding:16px 40px;
    width:100%; max-width:320px; text-align:center;
    border-bottom:1px solid rgba(245,158,11,0.07);
    transition:color 0.25s; letter-spacing:0.07em;
    touch-action:manipulation; min-height:64px;
  }
  .mobile-nav-btn:last-of-type { border-bottom:none; }
  .mobile-nav-btn:active { color:#f59e0b; }

  .mobile-cta-bar {
    position:fixed; bottom:0; left:0; right:0; z-index:90;
    display:none; gap:0;
    background:rgba(4,9,22,0.96); backdrop-filter:blur(20px);
    border-top:1px solid rgba(245,158,11,0.18);
    padding:10px 16px;
    padding-bottom:calc(10px + env(safe-area-inset-bottom));
    animation:mobileCta 0.5s cubic-bezier(0.2,0.8,0.4,1) 1s both;
  }
  @media (max-width:767px) {
    .mobile-cta-bar { display:flex; }
    .main-content { padding-bottom:72px; }
  }

  .whatsapp-float {
    position: fixed;
    bottom: 2rem;
    right: 2rem;
    z-index: 9000;
    display: flex;
    align-items: center;
    flex-direction: row-reverse;
    gap: 0.75rem;
    cursor: pointer;
    text-decoration: none;
  }

  .whatsapp-btn {
    width: 58px;
    height: 58px;
    background: #25D366;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 6px 24px rgba(37, 211, 102, 0.45);
    transition: transform 0.3s cubic-bezier(0.34,1.56,0.64,1), box-shadow 0.3s ease;
    position: relative;
    flex-shrink: 0;
  }

  .whatsapp-btn::before {
    content: '';
    position: absolute;
    inset: -4px;
    border-radius: 50%;
    border: 2px solid rgba(37, 211, 102, 0.5);
    animation: wa-ripple 2.2s ease-out infinite;
  }
  .whatsapp-btn::after {
    content: '';
    position: absolute;
    inset: -4px;
    border-radius: 50%;
    border: 2px solid rgba(37, 211, 102, 0.25);
    animation: wa-ripple 2.2s ease-out infinite 0.7s;
  }
  @keyframes wa-ripple {
    0%   { transform: scale(1);   opacity: 1; }
    100% { transform: scale(1.7); opacity: 0; }
  }

  .whatsapp-btn svg {
    width: 30px;
    height: 30px;
    fill: #ffffff;
    transition: transform 0.3s ease;
    position: relative;
    z-index: 1;
  }

  .whatsapp-float:hover .whatsapp-btn {
    transform: scale(1.12);
    box-shadow: 0 10px 36px rgba(37, 211, 102, 0.6);
  }
  .whatsapp-float:hover .whatsapp-btn svg {
    transform: rotate(-8deg) scale(1.1);
  }

  .whatsapp-tooltip {
    background: #ffffff;
    color: #111;
    font-family: 'Jost', sans-serif;
    font-size: 0.82rem;
    font-weight: 600;
    padding: 0.55rem 0.9rem;
    border-radius: 8px;
    box-shadow: 0 4px 20px rgba(0,0,0,0.18);
    white-space: nowrap;
    opacity: 0;
    transform: translateX(8px);
    transition: opacity 0.3s ease, transform 0.3s ease;
    pointer-events: none;
    line-height: 1.4;
    position: relative;
  }
  .whatsapp-tooltip span {
    display: block;
    font-size: 0.72rem;
    font-weight: 400;
    color: #555;
    margin-top: 1px;
  }
  .whatsapp-tooltip::before {
    content: '';
    position: absolute;
    right: -6px;
    top: 50%;
    transform: translateY(-50%);
    border-width: 6px 0 6px 6px;
    border-style: solid;
    border-color: transparent transparent transparent #ffffff;
  }

  .whatsapp-float:hover .whatsapp-tooltip {
    opacity: 1;
    transform: translateX(0);
  }

  .wa-online {
    position: absolute;
    top: 2px;
    right: 2px;
    width: 13px;
    height: 13px;
    background: #4ade80;
    border-radius: 50%;
    border: 2px solid #fff;
    animation: wa-blink 2.5s ease-in-out infinite;
    z-index: 1;
  }
  @keyframes wa-blink {
    0%,100% { opacity: 1; }
    50%      { opacity: 0.4; }
  }

  .whatsapp-float {
    animation: wa-enter 0.6s cubic-bezier(0.34,1.56,0.64,1) 2.2s both;
  }
  @keyframes wa-enter {
    from { opacity: 0; transform: translateY(30px) scale(0.7); }
    to   { opacity: 1; transform: translateY(0)   scale(1);   }
  }

  @media (max-width: 480px) {
    .whatsapp-float { bottom: 1.25rem; right: 1.25rem; }
    .whatsapp-btn   { width: 52px; height: 52px; }
    .whatsapp-btn svg { width: 26px; height: 26px; }
    .whatsapp-tooltip { display: none; }
  }
`;

/* ═══════════════════════════════════════════════════════
   SVG ICONS
═══════════════════════════════════════════════════════ */
function SunIcon({ size = 48, spinning = false }) {
  return (
    <svg width={size} height={size} viewBox="0 0 100 100"
      style={{ animation: spinning ? 'spinSlow 20s linear infinite' : 'none', flexShrink: 0 }}>
      {[0,30,60,90,120,150,180,210,240,270,300,330].map((a, i) => (
        <line key={i}
          x1={50 + 22 * Math.cos((a-90)*Math.PI/180)} y1={50 + 22 * Math.sin((a-90)*Math.PI/180)}
          x2={50 + 44 * Math.cos((a-90)*Math.PI/180)} y2={50 + 44 * Math.sin((a-90)*Math.PI/180)}
          stroke="#f59e0b" strokeWidth={i%2===0?2.5:1.5} strokeLinecap="round"
          opacity={i%2===0?0.9:0.5}
        />
      ))}
      <circle cx="50" cy="50" r="17" fill="#f97316" />
      <circle cx="50" cy="50" r="13" fill="#f59e0b" />
      <circle cx="50" cy="50" r="8"  fill="#fde68a" />
    </svg>
  );
}

function HamburgerIcon({ open }) {
  return (
    <svg width="26" height="26" viewBox="0 0 26 26" fill="none">
      <rect x="3" y={open ? 12 : 6}  width="20" height="2.2" rx="1.1" fill="#f59e0b"
        style={{ transition:'all 0.3s', transform:open?'rotate(45deg)':'none', transformOrigin:'12px 13px' }}/>
      <rect x="3" y="12" width="20" height="2.2" rx="1.1" fill="#f59e0b"
        style={{ transition:'all 0.3s', opacity:open?0:1 }}/>
      <rect x="3" y={open ? 12 : 18} width="20" height="2.2" rx="1.1" fill="#f59e0b"
        style={{ transition:'all 0.3s', transform:open?'rotate(-45deg)':'none', transformOrigin:'12px 13px' }}/>
    </svg>
  );
}

function SolarPanelSVG({ scale = 1 }) {
  return (
    <svg width={420*scale} height={360*scale} viewBox="0 0 420 360"
      style={{ filter:'drop-shadow(0 30px 80px rgba(245,158,11,0.25))' }}>
      <ellipse cx="210" cy="345" rx="140" ry="12" fill="rgba(245,158,11,0.08)"/>
      <rect x="40" y="60" width="340" height="220" rx="10" fill="rgba(8,20,50,0.9)" stroke="rgba(245,158,11,0.5)" strokeWidth="1.5"/>
      {Array.from({length:4},(_,row)=>Array.from({length:6},(_,col)=>(
        <g key={`${row}-${col}`}>
          <rect x={52+col*54} y={72+row*50} width={48} height={43} rx="3"
            fill={`hsl(${220+col*8},${60+row*8}%,${12+col*2}%)`} stroke="rgba(245,158,11,0.22)" strokeWidth="0.8"/>
          <line x1={54+col*54} y1={75+row*50} x2={72+col*54} y2={75+row*50} stroke="rgba(253,230,138,0.18)" strokeWidth="1.5"/>
          <line x1={54+col*54} y1={75+row*50} x2={54+col*54} y2={90+row*50} stroke="rgba(253,230,138,0.12)" strokeWidth="1"/>
        </g>
      )))}
      <polygon points="175,280 245,280 225,335 195,335" fill="rgba(100,120,160,0.5)" stroke="rgba(245,158,11,0.3)" strokeWidth="1"/>
      <rect x="165" y="334" width="90" height="8" rx="4" fill="rgba(245,158,11,0.25)"/>
      {[0,1,2,3,4].map(i=>(
        <line key={i} x1={-10+i*30} y1={i*20} x2={80+i*55} y2={80+i*30} stroke="rgba(245,158,11,0.15)" strokeWidth="1" strokeDasharray="6 4"/>
      ))}
      <circle cx="20" cy="20" r="14" fill="rgba(245,158,11,0.2)" stroke="rgba(245,158,11,0.6)" strokeWidth="1.5"/>
      <circle cx="20" cy="20" r="8" fill="rgba(245,158,11,0.6)"/>
      {[0,45,90,135,180,225,270,315].map((a,i)=>(
        <line key={i}
          x1={20+16*Math.cos(a*Math.PI/180)} y1={20+16*Math.sin(a*Math.PI/180)}
          x2={20+22*Math.cos(a*Math.PI/180)} y2={20+22*Math.sin(a*Math.PI/180)}
          stroke="rgba(245,158,11,0.7)" strokeWidth="1.5" strokeLinecap="round"/>
      ))}
    </svg>
  );
}

/* ═══════════════════════════════════════════════════════
   PARTICLES
═══════════════════════════════════════════════════════ */
function Particles({ count = 14 }) {
  const pts = useRef(
    Array.from({length:count},()=>({
      left:`${Math.random()*100}%`,
      size:`${1.5+Math.random()*3}px`,
      delay:`${Math.random()*8}s`,
      dur:`${8+Math.random()*12}s`,
    }))
  ).current;
  return (
    <div style={{position:'absolute',inset:0,overflow:'hidden',pointerEvents:'none',zIndex:1}}>
      {pts.map((p,i)=>(
        <div key={i} style={{
          position:'absolute',left:p.left,bottom:'-10px',
          width:p.size,height:p.size,borderRadius:'50%',
          background:i%3===0?'#f59e0b':i%3===1?'#f97316':'#fde68a',
          animation:`particleRise ${p.dur} ${p.delay} infinite linear`,opacity:0,
        }}/>
      ))}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════
   LOADING SCREEN
═══════════════════════════════════════════════════════ */
function LoadingScreen({ onComplete }) {
  const [phase, setPhase] = useState(0);
  useEffect(()=>{
    const t=[
      setTimeout(()=>setPhase(1),200),
      setTimeout(()=>setPhase(2),1800),
      setTimeout(()=>setPhase(3),3400),
      setTimeout(()=>onComplete(),4400),
    ];
    return()=>t.forEach(clearTimeout);
  },[]);

  const skies=[
    '#000',
    'linear-gradient(to top,#3d0800 0%,#000515 100%)',
    'linear-gradient(to top,#c44200 0%,#6b0a35 35%,#060d1f 100%)',
    'linear-gradient(to top,#f97316 0%,#d45200 20%,#7c1060 55%,#060d1f 100%)',
  ];
  return (
    <div style={{
      position:'fixed',inset:0,zIndex:9999,
      display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',
      background:skies[phase],transition:'background 1.4s ease',
      animation:phase===3?'fadeOut 1s ease 0.2s forwards':'none',overflow:'hidden',
    }}>
      <div style={{
        position:'absolute',bottom:'36%',left:0,right:0,height:'1px',
        background:'linear-gradient(to right,transparent,rgba(245,158,11,0.7),transparent)',
        opacity:phase>=1?1:0,transition:'opacity 1s',
        boxShadow:'0 0 40px rgba(245,158,11,0.4)',
      }}/>
      <div style={{
        position:'absolute',bottom:'36%',left:'50%',
        width:'clamp(75px,17vw,110px)',height:'clamp(75px,17vw,110px)',borderRadius:'50%',
        background:'radial-gradient(circle at 40% 40%,#fde68a 0%,#f59e0b 35%,#f97316 65%,rgba(249,115,22,0.3) 100%)',
        animation:phase>=1?'sunRiseAnim 2s cubic-bezier(0.2,0.6,0.4,1) forwards':'none',
        opacity:phase>=1?1:0,filter:'blur(1px)',
      }}>
        <div style={{
          position:'absolute',inset:'-20px',borderRadius:'50%',
          background:'radial-gradient(circle,rgba(249,115,22,0.4) 0%,transparent 70%)',
          animation:phase>=2?'rayPulse 2s ease infinite':'none',
        }}/>
      </div>
      {phase>=2&&[0,36,72,108,144,180,216,252,288,324].map((angle,i)=>(
        <div key={i} style={{
          position:'absolute',bottom:'calc(36% + 40px)',left:'50%',
          width:'2px',height:`${40+(i%3)*18}px`,
          background:'linear-gradient(to top,rgba(245,158,11,0.7),transparent)',
          transformOrigin:'bottom center',
          transform:`translateX(-50%) rotate(${angle}deg)`,
          borderRadius:'2px',animation:'rayPulse 3s ease infinite',
          animationDelay:`${i*0.06}s`,
        }}/>
      ))}
      <div style={{
        position:'relative',zIndex:10,textAlign:'center',marginTop:'-160px',
        animation:phase>=1?'logoReveal 1s ease 0.5s both':'none',
        opacity:phase>=1?undefined:0,padding:'0 20px',
      }}>
        <div style={{display:'flex',alignItems:'center',justifyContent:'center',gap:'12px',marginBottom:'8px'}}>
          <SunIcon size={40}/>
          <div>
            <div style={{
              fontFamily:"'Cinzel',serif",
              fontSize:'clamp(1.7rem,8vw,4.2rem)',fontWeight:'900',letterSpacing:'0.1em',
              color:'#fde68a',textShadow:'0 0 40px rgba(245,158,11,0.8),0 4px 20px rgba(0,0,0,0.5)',lineHeight:1,
            }}>MPS SOLARS</div>
            <div style={{fontFamily:"'Jost'",fontSize:'clamp(0.55rem,2.2vw,0.72rem)',letterSpacing:'0.4em',color:'rgba(253,230,138,0.7)',textTransform:'uppercase',marginTop:'6px'}}>
              Powering Karnataka's Future
            </div>
          </div>
        </div>
      </div>
      <div style={{
        position:'absolute',bottom:'10%',left:'50%',transform:'translateX(-50%)',
        width:'clamp(150px,45vw,220px)',height:'2px',
        background:'rgba(255,255,255,0.08)',borderRadius:'2px',overflow:'hidden',
        opacity:phase<3?1:0,transition:'opacity 0.5s',
      }}>
        <div style={{height:'100%',background:'linear-gradient(to right,#f59e0b,#fde68a,#f97316)',borderRadius:'2px',animation:phase>=1?'loadBar 3s linear forwards':'none'}}/>
      </div>
      <div style={{
        position:'absolute',bottom:'calc(10% + 14px)',
        fontFamily:"'Jost'",fontSize:'clamp(0.5rem,1.8vw,0.62rem)',
        color:'rgba(253,230,138,0.4)',letterSpacing:'0.22em',textTransform:'uppercase',
        opacity:phase<3?1:0,transition:'opacity 0.5s',textAlign:'center',
      }}>Initializing Solar Systems...</div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════
   NAVBAR
═══════════════════════════════════════════════════════ */
function Navbar({ scrolled }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const { isMobile } = useBreakpoint();
  const go = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior:'smooth' });
    setMenuOpen(false);
  };
  useEffect(()=>{
    document.body.style.overflow = menuOpen ? 'hidden' : '';
    return()=>{ document.body.style.overflow=''; };
  },[menuOpen]);

  return (
    <>
      <nav style={{
        position:'fixed',top:0,left:0,right:0,zIndex:100,
        display:'flex',alignItems:'center',justifyContent:'space-between',
        padding:isMobile?'14px 18px':'18px 60px',
        background:scrolled||menuOpen?'rgba(4,9,22,0.95)':'transparent',
        backdropFilter:scrolled||menuOpen?'blur(24px)':'none',
        borderBottom:scrolled?'1px solid rgba(245,158,11,0.07)':'none',
        transition:'all 0.4s ease',
      }}>
        <button onClick={()=>{window.scrollTo({top:0,behavior:'smooth'});setMenuOpen(false);}} style={{
          display:'flex',alignItems:'center',gap:'9px',
          background:'none',border:'none',cursor:'pointer',zIndex:101,
          touchAction:'manipulation',
        }}>
          <SunIcon size={isMobile?27:34} spinning/>
          <span style={{fontFamily:"'Cinzel',serif",fontSize:isMobile?'0.95rem':'1.15rem',fontWeight:'700',color:'#f59e0b',letterSpacing:'0.06em'}}>
            MPS SOLARS
          </span>
        </button>

        {!isMobile&&(
          <div style={{display:'flex',alignItems:'center',gap:'32px'}}>
            {[['Home','hero'],['Services','services'],['Calculator','calculator'],['Why Us','whyus'],['Contact','contact']].map(([l,id])=>(
              <button key={id} onClick={()=>go(id)} className="nav-link">{l}</button>
            ))}
            <button className="btn-gold" onClick={()=>go('contact')} style={{padding:'10px 22px',fontSize:'0.7rem'}}>Free Quote</button>
          </div>
        )}

        {isMobile&&(
          <button onClick={()=>setMenuOpen(o=>!o)} style={{
            background:'none',border:'none',cursor:'pointer',zIndex:101,
            padding:'7px',display:'flex',alignItems:'center',justifyContent:'center',
            minWidth:'44px',minHeight:'44px',touchAction:'manipulation',
          }}>
            <HamburgerIcon open={menuOpen}/>
          </button>
        )}
      </nav>

      {isMobile&&menuOpen&&(
        <div className="mobile-menu">
          <div style={{display:'flex',alignItems:'center',gap:'10px',marginBottom:'28px'}}>
            <SunIcon size={30}/>
            <span style={{fontFamily:"'Cinzel'",fontSize:'1.05rem',color:'#f59e0b',fontWeight:'700'}}>MPS SOLARS</span>
          </div>
          {[['Home','hero'],['Services','services'],['Calculator','calculator'],['Why Us','whyus'],['Contact','contact']].map(([l,id])=>(
            <button key={id} className="mobile-nav-btn" onClick={()=>go(id)}>{l}</button>
          ))}
          <button className="btn-gold" onClick={()=>go('contact')} style={{width:'100%',maxWidth:'320px',marginTop:'24px',padding:'18px',fontSize:'0.8rem'}}>
            ☀ Get Free Quote
          </button>
        </div>
      )}
    </>
  );
}

/* ═══════════════════════════════════════════════════════
   HERO
═══════════════════════════════════════════════════════ */
function HeroSection() {
  const { isMobile, isTablet } = useBreakpoint();
  const pad = isMobile ? '110px 20px 100px' : isTablet ? '120px 40px 80px' : '130px 60px 100px';
  return (
    <section id="hero" className="hero-bg" style={{
      minHeight:'100svh',position:'relative',
      display:'flex',alignItems:'center',
      padding:pad,overflow:'hidden',
    }}>
      <Particles count={isMobile?8:20}/>
      <div style={{
        position:'absolute',inset:0,zIndex:0,
        backgroundImage:`linear-gradient(rgba(245,158,11,0.03) 1px,transparent 1px),linear-gradient(90deg,rgba(245,158,11,0.03) 1px,transparent 1px)`,
        backgroundSize:isMobile?'40px 40px':'70px 70px',
      }}/>
      <div style={{position:'absolute',top:'15%',right:'5%',width:isMobile?'240px':'500px',height:isMobile?'240px':'500px',background:'radial-gradient(circle,rgba(245,158,11,0.1) 0%,transparent 65%)',borderRadius:'50%',animation:'pulse 5s ease infinite'}}/>

      <div style={{position:'relative',zIndex:2,maxWidth:'750px',width:'100%'}}>
        <div style={{
          display:'inline-flex',alignItems:'center',gap:'10px',
          padding:'7px 16px',
          background:'rgba(245,158,11,0.08)',border:'1px solid rgba(245,158,11,0.22)',
          borderRadius:'100px',marginBottom:'26px',
          animation:'fadeInUp 0.9s ease both',
        }}>
          <div style={{width:'7px',height:'7px',borderRadius:'50%',background:'#f59e0b',animation:'pulse 1.8s infinite'}}/>
          <span style={{fontFamily:"'Jost'",fontSize:isMobile?'0.6rem':'0.7rem',letterSpacing:'0.2em',color:'#f59e0b',textTransform:'uppercase'}}>
            Karnataka's #1 Solar Installer
          </span>
        </div>

        <h1 style={{
          fontFamily:"'Cinzel',serif",
          fontSize:'clamp(2rem,7.5vw,5.2rem)',
          fontWeight:'700',lineHeight:1.08,marginBottom:'20px',
          animation:'fadeInUp 0.9s ease 0.15s both',
        }}>
          <span style={{color:'#fff'}}>Best Solar Panels</span><br/>
          <span className="gold-text">in Bangalore & Karnataka</span><br/>
          <span style={{color:'#fff'}}>Trusted Solar EPC</span>
        </h1>

        <p style={{
          fontFamily:"'Jost'",fontSize:isMobile?'0.9rem':'1rem',lineHeight:1.85,
          color:'rgba(255,255,255,0.5)',maxWidth:'520px',marginBottom:'36px',
          animation:'fadeInUp 0.9s ease 0.3s both',
        }}>
          Top-rated solar panel installer & EPC provider. On-grid, off-grid & industrial solar systems. Free consultation. Subsidy assistance. MNRE & KREDL registered.
          Claim <strong style={{color:'#f59e0b'}}>₹78,000 GOCH government subsidy</strong> and transform your electricity bills into lifetime savings.
        </p>

        <div style={{
          display:'flex',gap:'12px',flexWrap:'wrap',
          flexDirection:isMobile?'column':'row',
          animation:'fadeInUp 0.9s ease 0.45s both',
        }}>
          <button className="btn-gold" onClick={()=>document.getElementById('calculator')?.scrollIntoView({behavior:'smooth'})}
            style={{flex:isMobile?'1':'auto'}}>
            ☀ Calculate My Savings
          </button>
          <button className="btn-outline-gold" onClick={()=>document.getElementById('services')?.scrollIntoView({behavior:'smooth'})}
            style={{flex:isMobile?'1':'auto'}}>
            Our Solutions →
          </button>
        </div>

        <div style={{
          display:'flex',gap:isMobile?'22px':'50px',flexWrap:'wrap',
          marginTop:isMobile?'48px':'78px',
          animation:'fadeInUp 0.9s ease 0.6s both',
        }}>
          {[['500+','Customers'],['10 MW+','Installed'],['₹78K','Subsidy'],['5★','Rating']].map(([n,l])=>(
            <div key={l}>
              <div style={{fontFamily:"'Cinzel'",fontSize:isMobile?'1.5rem':'2rem',fontWeight:'900',color:'#f59e0b',lineHeight:1}}>{n}</div>
              <div style={{fontFamily:"'Jost'",fontSize:'0.62rem',color:'rgba(255,255,255,0.3)',letterSpacing:'0.1em',textTransform:'uppercase',marginTop:'5px'}}>{l}</div>
            </div>
          ))}
        </div>
      </div>

      {!isMobile&&(
        <div style={{
          position:'absolute',right:isTablet?'2%':'4%',top:'50%',transform:'translateY(-50%)',
          animation:'float 7s ease infinite',zIndex:2,
        }}>
          <SolarPanelSVG scale={isTablet?0.7:1}/>
        </div>
      )}
    </section>
  );
}

/* ═══════════════════════════════════════════════════════
   SERVICES
═══════════════════════════════════════════════════════ */
const SERVICES=[
  {emoji:'⚡',title:'On-Grid Solar',sub:'Grid-Connected Power',accent:'#f59e0b',
   desc:'Connect to BESCOM and sell surplus electricity back via net metering. Zero battery needed — enjoy zero electricity bills with maximum ROI.',
   features:['Net Metering with BESCOM','ROI in 4–5 Years','No Battery Required','Up to ₹78,000 Subsidy','Remote Monitoring App'],
   grd:'linear-gradient(135deg,rgba(245,158,11,0.08),rgba(249,115,22,0.03))'},
  {emoji:'🔋',title:'Off-Grid Solar',sub:'Total Energy Freedom',accent:'#a78bfa',
   desc:'Complete independence with battery storage. Power your home 24/7 even during blackouts — ideal for rural areas and farms.',
   features:['Battery Storage Included','24/7 Uninterrupted Power','No Grid Dependency','Ideal for Remote Areas','Works in Any Weather'],
   grd:'linear-gradient(135deg,rgba(167,139,250,0.08),rgba(139,92,246,0.03))'},
  {emoji:'🏭',title:'Industrial Solar',sub:'Megawatt-Scale Solutions',accent:'#34d399',
   desc:'Custom-engineered MW-scale installations for factories, warehouses & commercial complexes. Slash costs by up to 90%.',
   features:['MW-Scale Installations','Up to 90% Cost Reduction','Custom Engineering','Priority 24/7 Maintenance','GST Input Tax Credit'],
   grd:'linear-gradient(135deg,rgba(52,211,153,0.08),rgba(16,185,129,0.03))'},
];

function ServicesSection() {
  const ref = useRef(null);
  const { isMobile, isTablet } = useBreakpoint();
  useEffect(()=>{
    const obs=new IntersectionObserver(
      entries=>entries.forEach(e=>{if(e.isIntersecting)e.target.classList.add('visible');}),
      {threshold:0.08}
    );
    ref.current?.querySelectorAll('.reveal').forEach(el=>obs.observe(el));
    return()=>obs.disconnect();
  },[]);
  const cols = isMobile?'1fr':isTablet?'1fr 1fr':'repeat(3,1fr)';
  return (
    <section id="services" ref={ref} style={{
      padding:isMobile?'80px 20px':'130px 60px',
      background:'linear-gradient(180deg,#060d1f 0%,#091428 50%,#060d1f 100%)',
    }}>
      <div className="reveal" style={{textAlign:'center',marginBottom:isMobile?'44px':'80px'}}>
        <span style={{fontFamily:"'Jost'",fontSize:'0.7rem',letterSpacing:'0.3em',color:'#f59e0b',textTransform:'uppercase'}}>What We Offer</span>
        <h2 style={{fontFamily:"'Cinzel',serif",fontSize:'clamp(1.8rem,5vw,3.5rem)',fontWeight:'700',color:'#fff',marginTop:'14px',marginBottom:'14px'}}>
          Solar Solutions For <span className="gold-text">Every Need</span>
        </h2>
        <p style={{fontFamily:"'Jost'",color:'rgba(255,255,255,0.4)',maxWidth:'480px',margin:'0 auto',lineHeight:1.9,fontSize:'0.9rem'}}>
          From single rooftop systems to industrial megawatt installations — designed, installed & maintained across Karnataka.
        </p>
      </div>
      <div style={{display:'grid',gridTemplateColumns:cols,gap:'22px',maxWidth:'1200px',margin:'0 auto'}}>
        {SERVICES.map((s,i)=>(
          <div key={i} className="glass-card reveal" style={{
            borderRadius:'18px',padding:isMobile?'28px 22px':'44px 38px',
            background:s.grd,position:'relative',overflow:'hidden',
            transitionDelay:`${i*0.1}s`,
          }}>
            <div style={{position:'absolute',top:0,right:0,width:'140px',height:'140px',background:`radial-gradient(circle at top right,${s.accent}14,transparent 70%)`,pointerEvents:'none'}}/>
            <div style={{width:'56px',height:'56px',borderRadius:'13px',background:`${s.accent}12`,border:`1px solid ${s.accent}35`,display:'flex',alignItems:'center',justifyContent:'center',fontSize:'26px',marginBottom:'20px'}}>{s.emoji}</div>
            <div style={{fontFamily:"'Jost'",fontSize:'0.66rem',letterSpacing:'0.22em',color:s.accent,textTransform:'uppercase',marginBottom:'7px'}}>{s.sub}</div>
            <h3 style={{fontFamily:"'Cinzel',serif",fontSize:isMobile?'1.25rem':'1.5rem',fontWeight:'700',color:'#fff',marginBottom:'13px'}}>{s.title}</h3>
            <p style={{fontFamily:"'Jost'",fontSize:'0.86rem',color:'rgba(255,255,255,0.46)',lineHeight:1.85,marginBottom:'22px'}}>{s.desc}</p>
            <div style={{display:'flex',flexDirection:'column',gap:'10px',marginBottom:'26px'}}>
              {s.features.map((f,j)=>(
                <div key={j} style={{display:'flex',alignItems:'center',gap:'11px'}}>
                  <div style={{width:'7px',height:'7px',borderRadius:'50%',background:s.accent,flexShrink:0,boxShadow:`0 0 6px ${s.accent}`}}/>
                  <span style={{fontFamily:"'Jost'",fontSize:'0.84rem',color:'rgba(255,255,255,0.68)'}}>{f}</span>
                </div>
              ))}
            </div>
            <button style={{
              width:'100%',padding:'14px',background:'transparent',
              border:`1px solid ${s.accent}50`,color:s.accent,
              fontFamily:"'Cinzel',serif",fontWeight:'600',fontSize:'0.7rem',
              letterSpacing:'0.12em',textTransform:'uppercase',
              borderRadius:'6px',cursor:'pointer',transition:'all 0.3s',
              minHeight:'48px',touchAction:'manipulation',
            }}
              onMouseEnter={e=>{e.target.style.background=`${s.accent}12`;e.target.style.borderColor=s.accent;}}
              onMouseLeave={e=>{e.target.style.background='transparent';e.target.style.borderColor=`${s.accent}50`;}}
            >Get Free Quote →</button>
          </div>
        ))}
      </div>
    </section>
  );
}

/* ═══════════════════════════════════════════════════════
   STATS
═══════════════════════════════════════════════════════ */
function AnimCounter({to,suffix='',label,color}) {
  const [val,setVal]=useState(0);
  const ref=useRef(null);
  useEffect(()=>{
    const obs=new IntersectionObserver(([entry])=>{
      if(!entry.isIntersecting)return;
      obs.disconnect();
      const steps=60,dur=1600;
      let cur=0;
      const timer=setInterval(()=>{
        cur+=to/steps;
        if(cur>=to){setVal(to);clearInterval(timer);}
        else setVal(Math.floor(cur));
      },dur/steps);
    },{threshold:0.5});
    if(ref.current)obs.observe(ref.current);
    return()=>obs.disconnect();
  },[to]);
  return (
    <div ref={ref} style={{textAlign:'center'}}>
      <div style={{fontFamily:"'Cinzel'",fontSize:'clamp(1.9rem,6vw,3.8rem)',fontWeight:'900',color,lineHeight:1}}>{val}{suffix}</div>
      <div style={{fontFamily:"'Jost'",fontSize:'0.66rem',color:'rgba(255,255,255,0.28)',letterSpacing:'0.12em',textTransform:'uppercase',marginTop:'10px'}}>{label}</div>
    </div>
  );
}

function StatsSection() {
  const { isMobile } = useBreakpoint();
  return (
    <section style={{
      padding:isMobile?'70px 20px':'110px 60px',
      background:'#08102b',
      borderTop:'1px solid rgba(245,158,11,0.07)',borderBottom:'1px solid rgba(245,158,11,0.07)',
      position:'relative',overflow:'hidden',
    }}>
      <div style={{position:'absolute',top:0,left:'50%',transform:'translateX(-50%)',width:'600px',height:'160px',background:'radial-gradient(ellipse,rgba(245,158,11,0.06) 0%,transparent 70%)'}}/>
      <div style={{
        maxWidth:'1100px',margin:'0 auto',position:'relative',
        display:'grid',gridTemplateColumns:isMobile?'1fr 1fr':'repeat(4,1fr)',gap:isMobile?'40px':'50px',
      }}>
        <AnimCounter to={500} suffix="+" label="Happy Customers"       color="#f59e0b"/>
        <AnimCounter to={10}  suffix=" MW+" label="Capacity Installed"  color="#fb923c"/>
        <AnimCounter to={8}   suffix="+ Yrs" label="Industry Experience" color="#fde68a"/>
        <AnimCounter to={98}  suffix="%"  label="Customer Satisfaction" color="#f59e0b"/>
      </div>
    </section>
  );
}

/* ═══════════════════════════════════════════════════════
   SOLAR CALCULATOR
═══════════════════════════════════════════════════════ */
function CalculatorSection() {
  const [bill,setBill]=useState(3000);
  const [sysType,setSysType]=useState('ongrid');
  const [result,setResult]=useState(null);
  const { isMobile } = useBreakpoint();
  const fmt=n=>'₹'+Math.round(n).toLocaleString('en-IN');

  useEffect(()=>{
    const tariff=7, sunHours=4.5;
    const costPerKw=sysType==='ongrid'?65000:sysType==='offgrid'?85000:55000;
    const monthlyUnits=bill/tariff;
    const systemKw=Math.max(1,Math.ceil((monthlyUnits/30/sunHours)*10)/10);
    const grossCost=systemKw*costPerKw;
    let centralSubsidy=0,stateSubsidy=0;
    if(sysType==='ongrid'){
      if(systemKw<=2) centralSubsidy=systemKw*30000;
      else if(systemKw<=3) centralSubsidy=60000+(systemKw-2)*18000;
      else centralSubsidy=78000;
      stateSubsidy=systemKw<=10?10000:0;
    }
    const netCost=Math.max(0,grossCost-centralSubsidy-stateSubsidy);
    const annualSaving=bill*12;
    const paybackYrs=(netCost/annualSaving).toFixed(1);
    setResult({systemKw,grossCost,centralSubsidy,stateSubsidy,netCost,monthlyUnits:Math.round(monthlyUnits),annualSaving,paybackYrs});
  },[bill,sysType]);

  const pct=((bill-500)/(50000-500))*100;

  return (
    <section id="calculator" style={{
      padding:isMobile?'80px 20px':'130px 60px',
      background:'linear-gradient(180deg,#060d1f 0%,#091428 100%)',
    }}>
      <div style={{maxWidth:'860px',margin:'0 auto'}}>
        <div style={{textAlign:'center',marginBottom:isMobile?'38px':'60px'}}>
          <span style={{fontFamily:"'Jost'",fontSize:'0.7rem',letterSpacing:'0.3em',color:'#f59e0b',textTransform:'uppercase'}}>Free Tool</span>
          <h2 style={{fontFamily:"'Cinzel',serif",fontSize:'clamp(1.8rem,5vw,3.2rem)',fontWeight:'700',color:'#fff',marginTop:'14px',marginBottom:'14px'}}>
            Solar <span className="gold-text">Savings Calculator</span>
          </h2>
          <p style={{fontFamily:"'Jost'",color:'rgba(255,255,255,0.4)',lineHeight:1.9,fontSize:'0.9rem',padding:'0 8px'}}>
            Enter your monthly electricity bill — get instant system size, cost & Karnataka GOCH subsidy
          </p>
        </div>

        <div style={{
          background:'rgba(255,255,255,0.02)',border:'1px solid rgba(245,158,11,0.18)',
          borderRadius:isMobile?'16px':'22px',padding:isMobile?'26px 18px':'56px',
          animation:'glowPulse 5s ease infinite',
        }}>
          {/* System type */}
          <div style={{marginBottom:'30px'}}>
            <label style={{fontFamily:"'Cinzel'",fontSize:'0.76rem',color:'rgba(255,255,255,0.5)',letterSpacing:'0.1em',display:'block',marginBottom:'12px'}}>
              System Type
            </label>
            <div style={{display:'flex',gap:'10px',flexWrap:'wrap'}}>
              {[['ongrid','⚡ On-Grid','#f59e0b'],['offgrid','🔋 Off-Grid','#a78bfa'],['industrial','🏭 Industrial','#34d399']].map(([v,l,c])=>(
                <button key={v} onClick={()=>setSysType(v)} style={{
                  flex:isMobile?'1 1 calc(33% - 10px)':'auto',
                  padding:isMobile?'12px 8px':'11px 22px',
                  background:sysType===v?`${c}18`:'rgba(255,255,255,0.03)',
                  border:`1px solid ${sysType===v?c:'rgba(255,255,255,0.1)'}`,
                  borderRadius:'100px',color:sysType===v?c:'rgba(255,255,255,0.4)',
                  fontFamily:"'Jost'",fontSize:isMobile?'0.76rem':'0.84rem',cursor:'pointer',
                  transition:'all 0.25s',minHeight:'44px',textAlign:'center',
                  touchAction:'manipulation',
                }}>
                  {l}
                </button>
              ))}
            </div>
            {sysType==='industrial'&&(
              <p style={{fontFamily:"'Jost'",fontSize:'0.72rem',color:'rgba(255,255,255,0.26)',marginTop:'9px'}}>
                * Subsidy not applicable for industrial installations.
              </p>
            )}
          </div>

          {/* Slider */}
          <div style={{marginBottom:'34px'}}>
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-end',marginBottom:'16px',gap:'10px'}}>
              <label style={{fontFamily:"'Cinzel'",fontSize:isMobile?'0.72rem':'0.82rem',color:'rgba(255,255,255,0.55)',letterSpacing:'0.07em',flexShrink:1}}>
                Monthly Bill
              </label>
              <div style={{fontFamily:"'Cinzel'",fontSize:isMobile?'1.7rem':'2.5rem',fontWeight:'900',color:'#f59e0b',lineHeight:1,whiteSpace:'nowrap'}}>
                {fmt(bill)}
              </div>
            </div>
            <input type="range" min="500" max="50000" step="100"
              value={bill} style={{'--pct':`${pct}%`}}
              onChange={e=>setBill(Number(e.target.value))}/>
            <div style={{display:'flex',justifyContent:'space-between',marginTop:'7px'}}>
              <span style={{fontFamily:"'Jost'",fontSize:'0.68rem',color:'rgba(255,255,255,0.22)'}}>₹500</span>
              <span style={{fontFamily:"'Jost'",fontSize:'0.68rem',color:'rgba(255,255,255,0.22)'}}>₹50,000</span>
            </div>
            {/* Quick picks */}
            <div style={{display:'flex',gap:'8px',flexWrap:'wrap',marginTop:'14px'}}>
              {[1000,2000,3000,5000,10000,20000].map(v=>(
                <button key={v} onClick={()=>setBill(v)} style={{
                  flex:isMobile?'1 1 calc(33% - 8px)':'0 0 auto',
                  padding:isMobile?'10px 4px':'8px 13px',
                  background:bill===v?'rgba(245,158,11,0.15)':'rgba(255,255,255,0.03)',
                  border:`1px solid ${bill===v?'#f59e0b':'rgba(255,255,255,0.08)'}`,
                  borderRadius:'100px',color:bill===v?'#f59e0b':'rgba(255,255,255,0.3)',
                  fontFamily:"'Jost'",fontSize:'0.74rem',cursor:'pointer',
                  transition:'all 0.2s',minHeight:'40px',touchAction:'manipulation',textAlign:'center',
                }}>
                  {fmt(v)}
                </button>
              ))}
            </div>
          </div>

          {/* Results */}
          {result&&(
            <>
              <div style={{
                background:'linear-gradient(135deg,rgba(245,158,11,0.1),rgba(249,115,22,0.05))',
                border:'1px solid rgba(245,158,11,0.28)',borderRadius:'13px',
                padding:isMobile?'26px 18px':'36px',textAlign:'center',marginBottom:'16px',
                position:'relative',overflow:'hidden',
              }}>
                <div style={{position:'absolute',inset:0,background:'radial-gradient(ellipse at 50% 0%,rgba(245,158,11,0.07),transparent 60%)'}}/>
                <div style={{fontFamily:"'Jost'",fontSize:'0.66rem',color:'#f59e0b',letterSpacing:'0.26em',textTransform:'uppercase',marginBottom:'4px'}}>
                  Recommended System
                </div>
                <div style={{fontFamily:"'Cinzel'",fontSize:'clamp(2.6rem,10vw,5rem)',fontWeight:'900',color:'#f59e0b',lineHeight:1}}>
                  {result.systemKw} kW
                </div>
                <div style={{fontFamily:"'Jost'",color:'rgba(255,255,255,0.32)',fontSize:'0.83rem',marginTop:'5px'}}>
                  ≈ {result.monthlyUnits} units / month
                </div>
              </div>

              <div style={{display:'grid',gridTemplateColumns:isMobile?'1fr':'repeat(auto-fit,minmax(175px,1fr))',gap:'11px',marginBottom:'16px'}}>
                {[
                  {l:'Gross System Cost',v:fmt(result.grossCost),c:'#ffffff'},
                  ...(sysType==='ongrid'?[
                    {l:'Central Subsidy (PM Surya Ghar)',v:`− ${fmt(result.centralSubsidy)}`,c:'#34d399'},
                    {l:'Karnataka KREDL Subsidy',v:`− ${fmt(result.stateSubsidy)}`,c:'#34d399'},
                  ]:[]),
                  {l:'Your Net Investment',v:fmt(result.netCost),c:'#f59e0b',big:true},
                  {l:'Annual Savings',v:fmt(result.annualSaving),c:'#a78bfa'},
                  {l:'Payback Period',v:`${result.paybackYrs} Years`,c:'#fb923c'},
                ].map((item,i)=>(
                  <div key={i} style={{
                    padding:isMobile?'14px 13px':'18px 16px',borderRadius:'10px',
                    border:item.big?'1px solid rgba(245,158,11,0.35)':'1px solid rgba(255,255,255,0.06)',
                    background:item.big?'rgba(245,158,11,0.07)':'rgba(255,255,255,0.02)',
                    display:isMobile?'flex':'block',justifyContent:'space-between',alignItems:'center',gap:'8px',
                  }}>
                    <div style={{fontFamily:"'Jost'",fontSize:'0.62rem',color:'rgba(255,255,255,0.3)',letterSpacing:'0.07em',textTransform:'uppercase',marginBottom:isMobile?0:'5px'}}>
                      {item.l}
                    </div>
                    <div style={{fontFamily:"'Cinzel'",fontSize:item.big?'1.2rem':'0.98rem',fontWeight:'700',color:item.c,whiteSpace:'nowrap'}}>
                      {item.v}
                    </div>
                  </div>
                ))}
              </div>

              <div style={{background:'rgba(52,211,153,0.05)',border:'1px solid rgba(52,211,153,0.15)',borderRadius:'10px',padding:'13px 16px',marginBottom:'22px'}}>
                <p style={{fontFamily:"'Jost'",fontSize:'0.74rem',color:'rgba(52,211,153,0.8)',lineHeight:1.8}}>
                  ✓ <strong>PM Surya Ghar Muft Bijli Yojana</strong> + Karnataka KREDL applied. Based on BESCOM ≈ ₹7/unit & 4.5 peak sun hours/day. Final subsidy subject to BESCOM/KREDL approval.
                </p>
              </div>

              <button className="btn-gold" onClick={()=>document.getElementById('contact')?.scrollIntoView({behavior:'smooth'})} style={{width:'100%',padding:'20px',fontSize:'0.8rem',borderRadius:'8px'}}>
                ☀ Get My Free Detailed Quote — No Obligation
              </button>
            </>
          )}
        </div>
      </div>
    </section>
  );
}

/* ═══════════════════════════════════════════════════════
   WHY US
═══════════════════════════════════════════════════════ */
const WHY_US=[
  {icon:'🏆',title:'Certified Installers',desc:'MNRE-certified engineers with 8+ years of solar expertise across Karnataka.'},
  {icon:'📋',title:'End-to-End Service',desc:'Site survey, design, installation, BESCOM approval & subsidy processing — we handle everything.'},
  {icon:'🛡️',title:'25-Year Warranty',desc:'Tier-1 panels with 25-year performance warranty & 5-year installation guarantee.'},
  {icon:'💰',title:'Best Price Promise',desc:'Competitive pricing with 0% EMI options available. No hidden costs, ever.'},
  {icon:'📱',title:'Smart Monitoring',desc:'Real-time mobile app to monitor your solar generation and savings live anytime.'},
  {icon:'🏛️',title:'Subsidy Experts',desc:'We handle all paperwork for Karnataka GOCH & PM Surya Ghar subsidies on your behalf.'},
];

function WhyUsSection() {
  const ref=useRef(null);
  const { isMobile, isTablet } = useBreakpoint();
  useEffect(()=>{
    const obs=new IntersectionObserver(
      entries=>entries.forEach(e=>{if(e.isIntersecting)e.target.classList.add('visible');}),
      {threshold:0.08}
    );
    ref.current?.querySelectorAll('.reveal').forEach(el=>obs.observe(el));
    return()=>obs.disconnect();
  },[]);
  const cols = isMobile?'1fr':isTablet?'1fr 1fr':'repeat(3,1fr)';
  return (
    <section id="whyus" ref={ref} style={{
      padding:isMobile?'80px 20px':'130px 60px',
      background:'linear-gradient(180deg,#091428 0%,#060d1f 100%)',
    }}>
      <div style={{maxWidth:'1200px',margin:'0 auto'}}>
        <div className="reveal" style={{textAlign:'center',marginBottom:isMobile?'44px':'70px'}}>
          <span style={{fontFamily:"'Jost'",fontSize:'0.7rem',letterSpacing:'0.3em',color:'#f59e0b',textTransform:'uppercase'}}>Why MPS Solars</span>
          <h2 style={{fontFamily:"'Cinzel',serif",fontSize:'clamp(1.8rem,5vw,3.2rem)',fontWeight:'700',color:'#fff',marginTop:'14px'}}>
            The <span className="gold-text">MPS Advantage</span>
          </h2>
        </div>
        <div style={{display:'grid',gridTemplateColumns:cols,gap:'18px'}}>
          {WHY_US.map((item,i)=>(
            <div key={i} className="glass-card reveal" style={{
              borderRadius:'14px',padding:isMobile?'22px 18px':'30px 26px',
              display:'flex',gap:'15px',alignItems:'flex-start',
              transitionDelay:`${i*0.07}s`,
            }}>
              <div style={{width:'48px',height:'48px',borderRadius:'11px',flexShrink:0,background:'rgba(245,158,11,0.07)',border:'1px solid rgba(245,158,11,0.16)',display:'flex',alignItems:'center',justifyContent:'center',fontSize:'21px'}}>
                {item.icon}
              </div>
              <div>
                <h4 style={{fontFamily:"'Cinzel',serif",fontSize:'0.9rem',fontWeight:'700',color:'#fff',marginBottom:'7px'}}>{item.title}</h4>
                <p style={{fontFamily:"'Jost'",fontSize:'0.83rem',color:'rgba(255,255,255,0.4)',lineHeight:1.8}}>{item.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ═══════════════════════════════════════════════════════
   FOOTER
═══════════════════════════════════════════════════════ */
function Footer() {
  const go=(id)=>document.getElementById(id)?.scrollIntoView({behavior:'smooth'});
  const { isMobile } = useBreakpoint();
  return (
    <footer id="contact" style={{
      background:'#02060f',borderTop:'1px solid rgba(245,158,11,0.07)',
      padding:isMobile?'64px 20px 36px':'90px 60px 44px',
    }}>
      <div style={{maxWidth:'1200px',margin:'0 auto'}}>
        <div style={{display:'grid',gridTemplateColumns:isMobile?'1fr':'repeat(auto-fit,minmax(225px,1fr))',gap:isMobile?'42px':'60px',marginBottom:'56px'}}>
          <div>
            <div style={{display:'flex',alignItems:'center',gap:'10px',marginBottom:'16px'}}>
              <SunIcon size={32} spinning/>
              <span style={{fontFamily:"'Cinzel'",fontSize:'1.05rem',fontWeight:'700',color:'#f59e0b',letterSpacing:'0.06em'}}>MPS SOLARS</span>
            </div>
            <p style={{fontFamily:"'Jost'",color:'rgba(255,255,255,0.3)',lineHeight:1.9,fontSize:'0.85rem',marginBottom:'18px'}}>
              Karnataka's trusted solar installer. Helping homes, businesses & industries harness clean energy since 2016.
            </p>
          </div>
          <div>
            <h4 style={{fontFamily:"'Cinzel'",color:'#fff',marginBottom:'18px',fontSize:'0.84rem',letterSpacing:'0.1em'}}>Services</h4>
            {['On-Grid Solar Systems','Off-Grid Solar Systems','Industrial Solar Plants','Solar AMC & Maintenance','Subsidy Assistance','Net Metering Help'].map(s=>(
              <div key={s} style={{fontFamily:"'Jost'",color:'rgba(255,255,255,0.34)',fontSize:'0.84rem',marginBottom:'12px',cursor:'pointer',transition:'color 0.3s',padding:'2px 0'}}
                onMouseEnter={e=>e.target.style.color='#f59e0b'}
                onMouseLeave={e=>e.target.style.color='rgba(255,255,255,0.34)'}
              >→ {s}</div>
            ))}
          </div>
          <div>
            <h4 style={{fontFamily:"'Cinzel'",color:'#fff',marginBottom:'18px',fontSize:'0.84rem',letterSpacing:'0.1em'}}>Contact Us</h4>
            {[['📞','+91 74111 07711'],['✉️','info@mpssolars.com'],['📍','Bangalore, Karnataka 560001'],['🕐','Mon – Sat: 9:00 AM – 6:00 PM'],['🏛️','MNRE & KREDL Registered']].map(([icon,text])=>(
              <div key={text} style={{display:'flex',gap:'11px',marginBottom:'13px',alignItems:'flex-start'}}>
                <span style={{fontSize:'0.9rem',flexShrink:0,marginTop:'2px'}}>{icon}</span>
                <span style={{fontFamily:"'Jost'",color:'rgba(255,255,255,0.4)',fontSize:'0.83rem',lineHeight:1.7}}>{text}</span>
              </div>
            ))}
          </div>
          <div>
            <h4 style={{fontFamily:"'Cinzel'",color:'#fff',marginBottom:'13px',fontSize:'0.84rem',letterSpacing:'0.1em'}}>Free Consultation</h4>
            <p style={{fontFamily:"'Jost'",color:'rgba(255,255,255,0.34)',fontSize:'0.83rem',lineHeight:1.8,marginBottom:'20px'}}>
              Site survey + detailed proposal within 24 hours — absolutely free.
            </p>
            <button className="btn-gold" onClick={()=>go('contact')} style={{width:'100%',marginBottom:'10px'}}>☀ Book Free Site Survey</button>
            <button className="btn-outline-gold" onClick={()=>go('calculator')} style={{width:'100%'}}>Calculate Savings First</button>
          </div>
        </div>
        <div style={{
          borderTop:'1px solid rgba(255,255,255,0.05)',paddingTop:'26px',
          display:'flex',justifyContent:'space-between',alignItems:'center',
          flexWrap:'wrap',gap:'12px',
          flexDirection:isMobile?'column':'row',
          textAlign:isMobile?'center':'left',
        }}>
          <span style={{fontFamily:"'Jost'",fontSize:'0.7rem',color:'rgba(255,255,255,0.16)'}}>
            © 2025 MPS Solars. All rights reserved. | Karnataka Solar Energy Company
          </span>
          <span style={{fontFamily:"'Jost'",fontSize:'0.68rem',color:'rgba(245,158,11,0.3)',letterSpacing:'0.1em'}}>
            ☀ Built on Sunshine · Powered by Purpose
          </span>
        </div>
      </div>
    </footer>
  );
}

/* ═══════════════════════════════════════════════════════
   MOBILE BOTTOM CTA BAR
═══════════════════════════════════════════════════════ */
function MobileCtaBar() {
  return (
    <div className="mobile-cta-bar">
      <a href="tel:+917411107711" style={{
        flex:1,display:'flex',alignItems:'center',justifyContent:'center',gap:'7px',
        padding:'13px 8px',
        background:'rgba(245,158,11,0.08)',border:'1px solid rgba(245,158,11,0.25)',
        borderRadius:'8px',color:'#f59e0b',
        fontFamily:"'Cinzel'",fontSize:'0.7rem',fontWeight:'700',letterSpacing:'0.1em',
        textDecoration:'none',textTransform:'uppercase',
        touchAction:'manipulation',minHeight:'50px',
      }}>
        📞 Call Now
      </a>
      <div style={{width:'10px'}}/>
      <button onClick={()=>document.getElementById('calculator')?.scrollIntoView({behavior:'smooth'})} style={{
        flex:1.5,
        background:'linear-gradient(135deg,#f59e0b,#f97316)',
        border:'none',borderRadius:'8px',color:'#060d1f',
        fontFamily:"'Cinzel'",fontSize:'0.7rem',fontWeight:'700',letterSpacing:'0.1em',
        cursor:'pointer',textTransform:'uppercase',
        touchAction:'manipulation',minHeight:'50px',
        transition:'all 0.25s',
      }}>
        ☀ Free Quote
      </button>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════
   FLOATING WHATSAPP ICON
═══════════════════════════════════════════════════════ */
function FloatingWhatsApp() {
  return (
    <a
      className="whatsapp-float"
      href="https://wa.me/917411107711?text=Hello%20MPS%20Solars!%20I%27d%20like%20to%20inquire%20about%20your%20solar%20services."
      target="_blank"
      rel="noopener noreferrer"
      aria-label="Chat with MPS Solars on WhatsApp"
    >
      <div className="whatsapp-btn">
        <svg viewBox="0 0 32 32" xmlns="http://www.w3.org/2000/svg">
          <path d="M16 0C7.163 0 0 7.163 0 16c0 2.833.738 5.494 2.027 7.8L0 32l8.418-2.004A15.938 15.938 0 0116 32c8.837 0 16-7.163 16-16S24.837 0 16 0zm0 29.333a13.27 13.27 0 01-6.79-1.864l-.487-.29-5.003 1.19 1.22-4.871-.32-.503A13.267 13.267 0 012.667 16C2.667 8.636 8.636 2.667 16 2.667S29.333 8.636 29.333 16 23.364 29.333 16 29.333zm7.27-9.953c-.397-.2-2.353-1.16-2.717-1.293-.364-.133-.63-.2-.894.2-.265.397-1.028 1.293-1.26 1.56-.232.265-.464.298-.862.1-.397-.2-1.678-.618-3.195-1.972-1.18-1.053-1.977-2.353-2.21-2.75-.232-.398-.025-.612.175-.81.179-.178.397-.464.596-.695.2-.232.265-.398.397-.663.133-.265.067-.497-.033-.696-.1-.2-.894-2.154-1.225-2.95-.322-.773-.65-.667-.894-.68l-.762-.013c-.265 0-.696.1-1.06.497-.364.398-1.392 1.36-1.392 3.314s1.425 3.845 1.623 4.11c.2.265 2.804 4.28 6.793 6.003.95.41 1.69.655 2.268.838.952.303 1.82.26 2.505.158.764-.114 2.353-.963 2.685-1.893.332-.93.332-1.727.232-1.893-.1-.166-.364-.265-.762-.464z"/>
        </svg>
        <div className="wa-online"></div>
      </div>
      <div className="whatsapp-tooltip">
        Chat with us
        <span>We reply within minutes</span>
      </div>
    </a>
  );
}

/* ═══════════════════════════════════════════════════════
   SEO META TAGS & STRUCTURED DATA
═══════════════════════════════════════════════════════ */
function SEOHead() {
  useEffect(() => {
    // Set meta tags dynamically
    const metaTags = [
      { name: 'description', content: 'MPS Solars - Top Solar Panel EPC Installer in Bangalore & Karnataka. On-grid & off-grid solar systems with subsidy assistance. Free consultation & site survey.' },
      { name: 'keywords', content: 'solar panels bangalore, solar EPC bangalore, solar systems karnataka, on-grid solar, off-grid solar, industrial solar plants, renewable energy karnataka, solar installation, rooftop solar, solar subsidy' },
      { name: 'viewport', content: 'width=device-width, initial-scale=1.0' },
      { name: 'theme-color', content: '#060d1f' },
      { name: 'author', content: 'MPS Solars' },
      { name: 'copyright', content: 'Copyright © 2025 MPS Solars. All rights reserved.' },
      { name: 'language', content: 'English' },
      { name: 'revisit-after', content: '7 days' },
      { name: 'google-site-verification', content: 'YOUR_GOOGLE_SEARCH_CONSOLE_CODE_HERE' },
      { property: 'og:type', content: 'website' },
      { property: 'og:title', content: 'MPS Solars - Top Solar Panel EPC Installer in Bangalore & Karnataka' },
      { property: 'og:description', content: 'Professional solar EPC services in Karnataka. On-grid, off-grid, and industrial solar solutions with guaranteed savings. Free site survey & subsidy assistance.' },
      { property: 'og:image', content: window.location.origin + '/solar-og-image.jpg' },
      { property: 'og:url', content: window.location.origin },
      { property: 'og:site_name', content: 'MPS Solars' },
      { property: 'og:locale', content: 'en_IN' },
      { name: 'twitter:card', content: 'summary_large_image' },
      { name: 'twitter:title', content: 'MPS Solars - Solar Panel Installer Bangalore' },
      { name: 'twitter:description', content: 'Professional solar EPC services. Free site survey & consultation.' },
      { name: 'twitter:image', content: window.location.origin + '/solar-og-image.jpg' },
      { name: 'robots', content: 'index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1, noarchive' },
      { name: 'googlebot', content: 'index, follow, snippet, archive' },
      { name: 'mobile-web-app-capable', content: 'yes' },
      { name: 'apple-mobile-web-app-capable', content: 'yes' },
      { name: 'apple-mobile-web-app-status-bar-style', content: 'black-translucent' },
      { name: 'application-name', content: 'MPS Solars' },
      { name: 'apple-mobile-web-app-title', content: 'MPS Solars' },
      { name: 'canonical', content: window.location.href },
    ];

    metaTags.forEach(tag => {
      let el = document.querySelector(`meta[${tag.property ? 'property' : 'name'}="${tag.property || tag.name}"]`);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(tag.property ? 'property' : 'name', tag.property || tag.name);
        document.head.appendChild(el);
      }
      el.content = tag.content;
    });

    // Set page title
    document.title = 'MPS Solars | Top Solar Panel EPC Installer in Bangalore & Karnataka';

    // Add link tags
    const linkTags = [
      { rel: 'icon', href: '/favicon.ico' },
      { rel: 'apple-touch-icon', href: '/apple-touch-icon.png' },
      { rel: 'manifest', href: '/manifest.json' },
      { rel: 'sitemap', href: '/sitemap.xml' },
      { rel: 'canonical', href: window.location.href },
      { rel: 'alternate', hrefLang: 'en-in', href: window.location.origin },
    ];

    linkTags.forEach(link => {
      let el = document.querySelector(`link[rel="${link.rel}"]`);
      if (!el) {
        el = document.createElement('link');
        el.rel = link.rel;
        document.head.appendChild(el);
      }
      if (link.href) el.href = link.href;
      if (link.hrefLang) el.hrefLang = link.hrefLang;
    });

    // Add Google Analytics 4 Script
    const ga4Script = document.createElement('script');
    ga4Script.async = true;
    ga4Script.src = 'https://www.googletagmanager.com/gtag/js?id=G-QM73YBWYJE';
    document.head.appendChild(ga4Script);

    // Initialize Google Analytics
    window.dataLayer = window.dataLayer || [];
    const gtag = function() {
      window.dataLayer.push(arguments);
    };
    gtag('js', new Date());
    gtag('config', 'G-QM73YBWYJE', {
      'page_path': window.location.pathname,
      'page_title': document.title,
      'anonymize_ip': true
    });

    // Add Google Search Console Verification (alternative method)
    const gscScript = document.createElement('meta');
    gscScript.name = 'google-site-verification';
    gscScript.content = 'YOUR_GOOGLE_SEARCH_CONSOLE_CODE_HERE'; // Replace with your verification code
    // Meta tag already added above, this is just for reference

    // Add JSON-LD Structured Data for Local Business
    const schemaScript = document.createElement('script');
    schemaScript.type = 'application/ld+json';
    schemaScript.innerHTML = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "LocalBusiness",
      "@id": window.location.origin,
      "name": "MPS Solars",
      "image": [
        window.location.origin + "/logo.png",
        window.location.origin + "/solar-og-image.jpg"
      ],
      "description": "Leading solar panel installer and EPC provider in Bangalore & Karnataka",
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "Bangalore, Karnataka",
        "addressLocality": "Bangalore",
        "addressRegion": "Karnataka",
        "postalCode": "560001",
        "addressCountry": "IN"
      },
      "telephone": "+919999999999",
      "email": "info@mpssolars.com",
      "url": window.location.origin,
      "priceRange": "₹₹₹",
      "areaServed": [
        { "@type": "State", "name": "Karnataka" },
        { "@type": "City", "name": "Bangalore" }
      ],
      "serviceType": ["Solar Panel Installation", "Solar EPC", "Solar Maintenance"],
      "knowsAbout": ["On-Grid Solar Systems", "Off-Grid Solar Systems", "Industrial Solar Plants", "Net Metering", "Subsidy Assistance"],
      "sameAs": [
        "https://www.facebook.com/mpssolars",
        "https://www.instagram.com/mpssolars",
        "https://www.linkedin.com/company/mpssolars"
      ],
      "ratingValue": "4.8",
      "ratingCount": "500",
      "aggregateRating": {
        "@type": "AggregateRating",
        "ratingValue": "4.8",
        "ratingCount": "500",
        "bestRating": "5",
        "worstRating": "1"
      }
    });
    document.head.appendChild(schemaScript);

    // Add JSON-LD for Organization
    const orgScript = document.createElement('script');
    orgScript.type = 'application/ld+json';
    orgScript.innerHTML = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "Organization",
      "@id": window.location.origin,
      "name": "MPS Solars",
      "alternateName": "MPS Solar",
      "url": window.location.origin,
      "logo": window.location.origin + "/logo.png",
      "description": "Karnataka's trusted solar installer since 2016",
      "foundingDate": "2016",
      "foundingLocation": "Bangalore, Karnataka",
      "numberOfEmployees": "50+",
      "contactPoint": {
        "@type": "ContactPoint",
        "contactType": "Customer Support",
        "telephone": "+919999999999",
        "email": "info@mpssolars.com",
        "availableLanguage": ["en"]
      },
      "sameAs": [
        "https://www.facebook.com/mpssolars",
        "https://www.instagram.com/mpssolars",
        "https://www.linkedin.com/company/mpssolars"
      ]
    });
    document.head.appendChild(orgScript);

    // Add JSON-LD for Service
    const serviceScript = document.createElement('script');
    serviceScript.type = 'application/ld+json';
    serviceScript.innerHTML = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "Service",
      "@id": window.location.origin + "#services",
      "name": "Solar Panel Installation & EPC Services",
      "provider": {
        "@type": "LocalBusiness",
        "name": "MPS Solars",
        "url": window.location.origin
      },
      "areaServed": [
        { "@type": "State", "name": "Karnataka" },
        { "@type": "City", "name": "Bangalore" }
      ],
      "serviceType": ["On-Grid Solar", "Off-Grid Solar", "Industrial Solar", "Subsidy Assistance", "Net Metering"],
      "description": "Professional solar panel installation and EPC services in Bangalore and Karnataka",
      "hasOfferCatalog": {
        "@type": "OfferCatalog",
        "name": "Solar Services",
        "itemListElement": [
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Service",
              "name": "On-Grid Solar Installation"
            }
          },
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Service",
              "name": "Off-Grid Solar Installation"
            }
          },
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Service",
              "name": "Industrial Solar Plants"
            }
          }
        ]
      }
    });
    document.head.appendChild(serviceScript);

    // Add JSON-LD for FAQPage (for SEO benefits)
    const faqScript = document.createElement('script');
    faqScript.type = 'application/ld+json';
    faqScript.innerHTML = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": [
        {
          "@type": "Question",
          "name": "What is the cost of solar installation in Bangalore?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Solar installation costs vary based on system size and type. On-grid systems cost around ₹65,000 per kW. We offer free site survey and customized quotes for your needs."
          }
        },
        {
          "@type": "Question",
          "name": "How much government subsidy can I get?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "You can get up to ₹78,000 under PM Surya Ghar Muft Bijli Yojana plus Karnataka KREDL subsidy. We handle all paperwork for subsidy approval."
          }
        },
        {
          "@type": "Question",
          "name": "What is the payback period for solar panels?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Typically 4-5 years for on-grid systems, after which you generate free electricity for 25+ years."
          }
        }
      ]
    });
    document.head.appendChild(faqScript);

  }, []);

  return null;
}

/* ═══════════════════════════════════════════════════════
   APP
═══════════════════════════════════════════════════════ */
export default function MPSSolars() {
  const [loading,setLoading]=useState(true);
  const [scrolled,setScrolled]=useState(false);
  useEffect(()=>{
    const fn=()=>setScrolled(window.scrollY>80);
    window.addEventListener('scroll',fn,{passive:true});
    return()=>window.removeEventListener('scroll',fn);
  },[]);
  return (
    <>
      <style>{STYLES}</style>
      <SEOHead/>
      {loading&&<LoadingScreen onComplete={()=>setLoading(false)}/>}
      {!loading&&(
        <>
          <div className="main-content" style={{minHeight:'100vh'}}>
            <Navbar scrolled={scrolled}/>
            <HeroSection/>
            <ServicesSection/>
            <StatsSection/>
            <CalculatorSection/>
            <WhyUsSection/>
            <Footer/>
          </div>
          <MobileCtaBar/>
          <FloatingWhatsApp/>
        </>
      )}
    </>
  );
}